import type { AnswerKeySelection, Grading } from "@memorang/types";
import { getOrderingMaxPoints } from "../ordering";

const positive = (total: number): number | undefined =>
	total > 0 ? total : undefined;

const getAnswerKeySelectionMaximumScore = (
	scoring: AnswerKeySelection,
): number | undefined => {
	if (scoring.points != null) {
		return scoring.points;
	}
	return positive(
		scoring.answerKey
			.filter((key) => key.isCorrect)
			.reduce((sum, key) => sum + (key.points ?? 0), 0),
	);
};

// CMS `maximumScore` is a normalized scale on some item types (e.g. the 5-point
// dictation scale), so it is the last resort behind every raw signal.
const getCmsMaximumScore = (grading: Grading): number | undefined =>
	"maximumScore" in grading && grading.maximumScore != null
		? grading.maximumScore
		: undefined;

const getTypelessPoints = (grading: Grading): number | undefined =>
	"points" in grading && typeof grading.points === "number"
		? grading.points
		: undefined;

const resolveTypedMaxScore = (grading: Grading): number | undefined => {
	switch (grading.type) {
		case "binary":
			return grading.points ?? 1;

		case "ANSWER_KEY_SELECTION":
		case "choice":
			return getAnswerKeySelectionMaximumScore(grading);

		// `points` can be authored on a different scale than the key it ships with,
		// so the answer-key total wins.
		case "ANSWER_KEY_MAPPING":
		case "ANSWER_KEY_TEXT": {
			const answerKeyTotal = positive(
				grading.answerKey.reduce((sum, key) => sum + (key.points ?? 0), 0),
			);
			return answerKeyTotal ?? grading.points;
		}

		case "ANSWER_KEY_ORDERING":
			return positive(getOrderingMaxPoints(grading.answerKey));

		case "ANSWER_KEY_ACCEPTED_RESPONSE":
		case "ANSWER_KEY_BLANK":
			return "maxPoints" in grading
				? (grading.maxPoints as number)
				: (grading.points ?? 0);

		case "selection":
		case "form":
			return (
				grading.points?.max ??
				grading.expectedResponses.reduce(
					(sum: number, response: { points: number }) => sum + response.points,
					0,
				)
			);

		case "criteria":
			return Object.values(grading.criteria).reduce(
				(sum: number, criterion: { maxPoints: number }) =>
					sum + criterion.maxPoints,
				0,
			);

		case "AI_RUBRIC":
			return (
				grading.points ??
				Object.values(grading.criteria).reduce(
					(sum, criterion) =>
						sum + (criterion.maxPoints ?? criterion.points?.max ?? 0),
					0,
				)
			);

		case "points":
			return grading.totalPoints;

		default:
			return undefined;
	}
};

export const resolveMaxScore = (
	grading: Grading | undefined,
): number | undefined => {
	if (grading == null) {
		return undefined;
	}
	const raw = grading.type
		? resolveTypedMaxScore(grading)
		: getTypelessPoints(grading);
	return raw ?? getCmsMaximumScore(grading);
};
